import { useCallback, useEffect, useRef } from 'react';
import {
  evictOld as defaultEvictOld,
  setViewport,
} from '@/features/canvas/engine/viewport/viewport-store';

const DEBOUNCE_MS = 500;
const LRU_CAP = 200;

// Module-level flag — eviction runs at most once per (user, page-load).
const evictedThisSession = new Set<string>();

// Test-only escape hatch. Safe to ship: the function only mutates a Set used as a debounce flag.
export function _clearEvictedSessionForTesting(): void {
  evictedThisSession.clear();
}

export interface UseViewportPersistenceArgs {
  canvasId: string;
  userId: string;
  /** Test-only DI seam — defaults to the imported evictOld. */
  evictOldFn?: (userId: string, max: number) => Promise<void>;
}

export interface UseViewportPersistenceResult {
  onViewportChange: (v: { x: number; y: number; zoom: number }) => void;
}

/**
 * Save-only viewport persistence hook (Option C).
 *
 * - Returns a debounced `onViewportChange` callback the engine wires into
 *   `<ReactFlow onViewportChange={...}>`. Rapid pan/zoom events coalesce
 *   into a single IDB write after a 500ms quiet window.
 * - On first mount per (user, page-load), schedules a one-shot LRU eviction
 *   capping the user's stored viewports at 200 entries.
 *
 * Restore is the SHELL's responsibility (await `getViewport(canvasId, userId)`
 * on mount, pass the result as `defaultViewport` to CanvasEngine). Splitting
 * restore out of the hook keeps the engine/shell seam clean: the engine
 * receives a resolved initial viewport as a prop and emits change events as
 * a callback — no imperative `reactFlow.setViewport()` handoff required.
 */
export function useViewportPersistence({
  canvasId,
  userId,
  evictOldFn = defaultEvictOld,
}: UseViewportPersistenceArgs): UseViewportPersistenceResult {
  const pendingRef = useRef<{ x: number; y: number; zoom: number } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Per-session eviction. The Set guard ensures this runs once per
  // (user, page-load) regardless of how many canvases the user opens.
  useEffect(() => {
    if (evictedThisSession.has(userId)) return;
    evictedThisSession.add(userId);
    void evictOldFn(userId, LRU_CAP);
  }, [userId, evictOldFn]);

  const flush = useCallback(() => {
    timerRef.current = null;
    if (!pendingRef.current) return;
    const v = pendingRef.current;
    pendingRef.current = null;
    void setViewport(canvasId, userId, v);
  }, [canvasId, userId]);

  const onViewportChange = useCallback(
    (v: { x: number; y: number; zoom: number }) => {
      pendingRef.current = v;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, DEBOUNCE_MS);
    },
    [flush],
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return { onViewportChange };
}
